// What needs you, for PCs that start from the boot server: each problem as one plain sentence about the PCs it is about,
// worked out from the boot summary of every PC on the floor and the boot server's own status. Pure — no I/O — so each rule
// is tested on its own. The cards, their actions and who sees them are in `BootProblems.tsx`.
//
// A PC staff took out of order says only that, so it is never in a card (one tile, one state). A state the boot server has
// stopped reporting is `unknown` and never in a card either: the boot server's own card says it went quiet.

import type { BootFloorPc, BootServer } from '@clavian/api-client';

import { isDiskLow } from '../boot/format';

export type BootProblemKind = 'noImage' | 'licence' | 'noServer' | 'serverOffline' | 'otherBoard' | 'startFailed' | 'notReaching' | 'letter' | 'behind' | 'diskLow';

export interface BootProblemCard {
  /** Stable across reads, so a card keeps its place while its numbers change. */
  readonly id: string;
  readonly kind: BootProblemKind;
  readonly tone: 'danger' | 'warn';
  /** The stations it is about, by number, ascending. Empty for a problem of the boot server itself. */
  readonly numbers: readonly number[];
  readonly stationIds: readonly string[];
  readonly bootPcIds: readonly string[];
  /** When it began, where known: the oldest of its PCs' live state, or when the boot server was last seen. */
  readonly sinceIso: string | null;
  readonly params: Readonly<Record<string, string | number>>;
}

/** A station on the floor, as far as a card needs it. */
export interface BootProblemStation {
  readonly stationId: string;
  readonly number: number;
  readonly classId: string;
}

/** An image is behind on this many PCs or more before it is a card of its own (fewer stay on their tiles and the filter). */
export const BEHIND_CARD_MIN = 3;

const oldest = (values: readonly (string | null | undefined)[]): string | null => {
  const known = values.filter((value): value is string => typeof value === 'string');
  return known.length === 0 ? null : known.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b));
};

export function bootProblemCards(input: {
  readonly stations: readonly BootProblemStation[];
  readonly pcs: readonly BootFloorPc[];
  readonly servers: readonly BootServer[];
  readonly classNames: ReadonlyMap<string, string>;
}): BootProblemCard[] {
  const station = new Map(input.stations.map((one) => [one.stationId, one] as const));
  // Only PCs on a station that is in use (not out of order) are about.
  const pcs = input.pcs.filter((pc) => station.has(pc.stationId));
  const server = input.servers[0];
  const cards: BootProblemCard[] = [];

  const about = (list: readonly BootFloorPc[]) => {
    const stations = list.map((pc) => station.get(pc.stationId)).filter((one): one is BootProblemStation => one !== undefined).sort((a, b) => a.number - b.number);
    return {
      numbers: stations.map((one) => one.number),
      stationIds: stations.map((one) => one.stationId),
      bootPcIds: stations.map((one) => list.find((pc) => pc.stationId === one.stationId)?.bootPcId ?? ''),
      sinceIso: oldest(list.map((pc) => pc.stateSince)),
    };
  };

  const cant = (problem: NonNullable<BootFloorPc['problem']>) => pcs.filter((pc) => pc.state === 'cant_start' && pc.problem === problem);

  // A PC that cannot start, no image for it: one card per class, since the fix is that class's.
  const noImage = cant('no_image');
  const byClass = new Map<string, BootFloorPc[]>();
  for (const pc of noImage) {
    const classId = station.get(pc.stationId)?.classId ?? '';
    byClass.set(classId, [...(byClass.get(classId) ?? []), pc]);
  }
  for (const [classId, list] of byClass) {
    const className = input.classNames.get(classId);
    cards.push({ id: `noImage:${classId}`, kind: 'noImage', tone: 'danger', ...about(list), params: className === undefined ? {} : { class: className } });
  }

  const noServer = cant('no_boot_server');
  if (noServer.length > 0) cards.push({ id: 'noServer', kind: 'noServer', tone: 'danger', ...about(noServer), params: {} });

  const licence = cant('licence_expired');
  if (licence.length > 0) cards.push({ id: 'licence', kind: 'licence', tone: 'danger', ...about(licence), params: {} });

  // The boot server itself: not seen for a while. PCs that say so are covered by it.
  const offlinePcs = cant('boot_server_offline');
  if (server?.status === 'offline' || offlinePcs.length > 0) {
    const last = server?.lastSeenAt ?? null;
    cards.push({ id: 'serverOffline', kind: 'serverOffline', tone: 'danger', numbers: [], stationIds: [], bootPcIds: [], sinceIso: last, params: {} });
  }

  const board = cant('other_motherboard');
  if (board.length > 0) cards.push({ id: 'otherBoard', kind: 'otherBoard', tone: 'danger', ...about(board), params: {} });

  const failed = cant('start_failed');
  if (failed.length > 0) cards.push({ id: 'startFailed', kind: 'startFailed', tone: 'danger', ...about(failed), params: {} });

  // PCs the boot server woke that no network start was seen from.
  const notReaching = pcs.filter((pc) => pc.livePhase === 'not_reaching');
  if (notReaching.length > 0) cards.push({ id: 'notReaching', kind: 'notReaching', tone: 'warn', ...about(notReaching), params: {} });

  // A game disk's letter another drive of the PC holds: one card per letter.
  const letters = new Map<string, BootFloorPc[]>();
  for (const pc of pcs) {
    if (pc.gameDiskLetterBlocked == null) continue;
    letters.set(pc.gameDiskLetterBlocked, [...(letters.get(pc.gameDiskLetterBlocked) ?? []), pc]);
  }
  for (const [letter, list] of [...letters].sort(([a], [b]) => a.localeCompare(b))) {
    cards.push({ id: `letter:${letter}`, kind: 'letter', tone: 'warn', ...about(list), params: { letter } });
  }

  // An image behind on many PCs.
  const behind = new Map<string, { readonly name: string; readonly version: number | null; readonly list: BootFloorPc[] }>();
  for (const pc of pcs) {
    if (pc.state !== 'update_waiting' || pc.image === null || pc.editing) continue;
    const entry = behind.get(pc.image.id) ?? { name: pc.image.name, version: pc.image.currentVersionNumber, list: [] };
    entry.list.push(pc);
    behind.set(pc.image.id, entry);
  }
  for (const [imageId, entry] of behind) {
    if (entry.list.length < BEHIND_CARD_MIN) continue;
    cards.push({ id: `behind:${imageId}`, kind: 'behind', tone: 'warn', ...about(entry.list), params: { image: entry.name, version: entry.version ?? 0, imageId } });
  }

  // A boot server drive nearly full: the same "running low" rule as the Boot server page.
  if (server !== undefined) {
    for (const disk of server.disks) {
      if (!isDiskLow(disk)) continue;
      cards.push({ id: `diskLow:${disk.role}`, kind: 'diskLow', tone: 'warn', numbers: [], stationIds: [], bootPcIds: [], sinceIso: null, params: { role: disk.role, freeBytes: disk.freeBytes } });
    }
  }
  return cards;
}

/** Stations whose update-waiting state a `behind` card already covers: they are not listed again. */
export function coveredByCards(cards: readonly BootProblemCard[]): ReadonlySet<string> {
  return new Set(cards.filter((card) => card.kind === 'behind').flatMap((card) => card.stationIds));
}
